import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createNocTypeTableColumns } from '../../components/noc-type-table-columns/noc-type-table-columns';
import { NocTypeFormDialog } from '../../components/noc-type-form-dialog/noc-type-form-dialog';
import { useDeleteNocType, useGetNocTypes } from '../../hooks/use-noc-types';
import { DEFAULT_NOC_TYPES_ORDER } from '../../graphql/queries';
import { NocType, NocTypesPagingResult } from '../../types/noc-type.types';

export const NocTypeManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.NOC_TYPES_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.NOC_TYPES_UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SETTINGS.NOC_TYPES_DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_NOC_TYPES_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; nocType: NocType | null }>({
    open: false,
    nocType: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; nocType: NocType | null }>({
    open: false,
    nocType: null,
  });

  const { data, isLoading, error } = useGetNocTypes({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { NocTypes?: NocTypesPagingResult })?.NocTypes;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteNocType, isPending: isDeleting } = useDeleteNocType();

  const handleDeleteConfirm = async () => {
    if (!deleteState.nocType) return;
    await deleteNocType(deleteState.nocType.ItemId);
    setDeleteState({ open: false, nocType: null });
  };

  const columns = createNocTypeTableColumns({
    t,
    onEdit: (nocType) => setFormState({ open: true, nocType }),
    onDelete: (nocType) => setDeleteState({ open: true, nocType }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('NOC_TYPE_MANAGER')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_NOC_TYPE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, nocType: null }),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <NocTypeFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, nocType: open ? prev.nocType : null }))
        }
        nocType={formState.nocType}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, nocType: open ? deleteState.nocType : null })
        }
        title={t('DELETE_NOC_TYPE')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
